<script setup lang="ts">
import { computed, reactive, ref, toRef, watch } from "vue";
import { useTranslation } from "i18next-vue";

import type { Finding } from "@/types/findings.types";
import type { Section } from "@/types/sections.types";
import {
  useUpdateFinding,
  useGetUnsortedFindings,
} from "@/stores/queries/findings.query";
import { useGetSectionFindings } from "@/stores/queries/sections.query";
import { useGetReportSectionsTree } from "@/stores/queries";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import type { DisorderTemplate, DisorderGrade } from "@/types/catalogues.types";
import CatalogueSessionState from "@/stores/report/CatalogueSessionState";
import { useReportNotationSystem } from "@/composable/useReportNotationSystem";
import { convertGrade } from "@/constants/notationSystems";
import NotationSystemBadge from "../NotationSystemBadge.vue";
import DisorderGradeBadge from "@/components/UI/DisorderGradeBadge.vue";
import FindingImagesTab from "./FindingImagesTab.vue";
import CatalogueSearchTab from "./CatalogueSearchTab.vue";
import SourceObservationDrawer from "../SourceObservationDrawer.vue";

const props = defineProps<{
  modelValue?: boolean;
  finding: Finding;
  cataloguePartId?: number;
  sectionId: number | null;
  diagnosticId: number;
  reportId: number;
}>();

const { t } = useTranslation();

const id = computed(() => props.finding.id);
const sectionIdRef = computed(() => props.sectionId ?? 0);
const reportIdRef = toRef(props, "reportId");

// Section title paths for location placeholder
const { data: sectionsTree } = useGetReportSectionsTree(reportIdRef);

const sectionTitlePaths = computed(() => {
  const map = new Map<number, string>();
  if (!sectionsTree.value) return map;
  const build = (sections: Section[], parentPath?: string) => {
    for (const section of sections) {
      const path = parentPath
        ? `${parentPath} > ${section.title}`
        : section.title;
      map.set(section.id, path);
      if (section.children?.length) {
        build(section.children, path);
      }
    }
  };
  build(sectionsTree.value);
  return map;
});

const defaultLocationPath = computed(() => {
  if (!props.sectionId) return "";
  return sectionTitlePaths.value.get(props.sectionId) ?? "";
});

const { mutateAsync: mutateUpdate } = useUpdateFinding(id);

// Get findings from query to have reactive images
// Use section findings if sectionId exists, otherwise use unsorted findings
const { data: sectionFindings } = useGetSectionFindings(sectionIdRef);
const { data: unsortedFindings } = useGetUnsortedFindings(reportIdRef);

// Derive current finding's images from query data (reactive)
// Now uses reportImages instead of diagnosticImages
const currentFindingImages = computed(() => {
  const findings = props.sectionId
    ? sectionFindings.value
    : unsortedFindings.value;
  const currentFinding = findings?.find((f) => f.id === props.finding.id);
  return currentFinding?.reportImages ?? props.finding?.reportImages ?? [];
});

// Tab state (persisted in session)
type TabName = "images" | "catalogue";
const activeTab = ref<TabName>(CatalogueSessionState.lastTab);

const setActiveTab = (tab: TabName) => {
  activeTab.value = tab;
  CatalogueSessionState.setTab(tab);
};

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const show = computed({
  get: () => props.modelValue,
  set: (value) => {
    emits("update:modelValue", value);
  },
});

const form = reactive({
  name: props.finding?.name ?? "",
  defaultNumbering: props.finding?.defaultNumbering ?? "",
  comment: props.finding?.comment ?? "",
  evolution: props.finding?.evolution ?? false,
  security: props.finding?.security ?? false,
  notation: props.finding?.notation ?? "",
  location: props.finding?.location ?? "",
  disorderTemplateId: props.finding?.disorderTemplateId ?? null,
  disorderGradeId: props.finding?.disorderGradeId ?? null,
});

watch(
  show,
  (newValue) => {
    if (newValue) {
      // Reset form when dialog opens
      form.name = props.finding?.name ?? "";
      form.defaultNumbering = props.finding?.defaultNumbering ?? "";
      form.comment = props.finding?.comment ?? "";
      form.evolution = props.finding?.evolution ?? false;
      form.security = props.finding?.security ?? false;
      form.notation = props.finding?.notation ?? "";
      form.location = props.finding?.location ?? "";
      form.disorderTemplateId = props.finding?.disorderTemplateId ?? null;
      form.disorderGradeId = props.finding?.disorderGradeId ?? null;
      // Keep the persisted tab from session state
      activeTab.value = CatalogueSessionState.lastTab;
    }
  },
  { immediate: true },
);

const applyDisorderGrade = (
  disorder: DisorderTemplate,
  grade: DisorderGrade,
) => {
  form.name = disorder.title;
  form.defaultNumbering = disorder.code ?? "";
  if (!form.comment.trim()) {
    form.comment = grade.description || disorder.description || "";
  }
  form.notation = grade.class;
  form.disorderTemplateId = disorder.id;
  form.disorderGradeId = grade.id;
};

const applyDisorder = (disorder: DisorderTemplate) => {
  form.name = disorder.title;
  form.defaultNumbering = disorder.code ?? "";
  if (!form.comment.trim()) {
    form.comment = disorder.description || "";
  }
  form.disorderTemplateId = disorder.id;
  form.disorderGradeId = null;
};

const confirm = () => {
  mutateUpdate(
    {
      name: form.name,
      defaultNumbering: form.defaultNumbering,
      comment: form.comment,
      evolution: form.evolution,
      security: form.security,
      notation: form.notation,
      location: form.location,
      disorderTemplateId: form.disorderTemplateId,
      disorderGradeId: form.disorderGradeId,
    },
    {
      onSuccess: () => {
        show.value = false;
      },
    },
  );
};

// Le système de notation est mémorisé au niveau du rapport : toutes les saisies
// de notes en parlent le même, formulaire comme tableau des constatations.
const {
  systemCode,
  isCustom,
  gradeOptions: notationOptions,
  setSystemCode,
  systemCodeOfValue,
} = useReportNotationSystem(reportIdRef);

// Changer de système reporte la note déjà saisie : vers un système classique en
// prenant l'équivalent de même gravité, vers la notation libre en reprenant le
// libellé tel quel.
const handleSystemChange = (code: string) => {
  const previous = systemCodeOfValue(form.notation);
  setSystemCode(code);
  if (form.notation && previous !== code) {
    form.notation = convertGrade(form.notation, code);
  }
};

// Toggle notation (clicking selected value deselects it)
const toggleNotation = (value: string) => {
  form.notation = form.notation === value ? "" : value;
};

// ============================================================
// IQOA Suggestions (multi-candidates)
// ============================================================
// Always read suggestions from the freshly fetched finding (sectionFindings or
// unsortedFindings) to stay reactive when the IQOA module finishes generating.
const currentFindingFresh = computed(() => {
  const findings = props.sectionId
    ? sectionFindings.value
    : unsortedFindings.value;
  return findings?.find((f) => f.id === props.finding.id) ?? props.finding;
});

const iqoaSuggestions = computed(
  () => currentFindingFresh.value?.iqoaSuggestions ?? [],
);

const hasIQOASuggestions = computed(() => iqoaSuggestions.value.length > 0);

// Badge expanded state (collapsed by default)
const isSuggestionsExpanded = ref(false);

// Source observations drawer
const showObservationDrawer = ref(false);
const sourceObservations = computed(
  () => currentFindingFresh.value?.sourceObservations ?? [],
);

// Helper: build part hierarchy info from a DisorderTemplate.part
const buildPartInfo = (template: DisorderTemplate | undefined | null) => {
  const part = template?.part;
  const catalogue = part?.catalogue;

  const partHierarchy: string[] = [];
  const partHierarchyObjects: { id: number; parentId?: number | null }[] = [];

  if (part) {
    if (part.parent?.parent) {
      partHierarchy.push(part.parent.parent.name);
      partHierarchyObjects.push({
        id: part.parent.parent.id,
        parentId: part.parent.parent.parentId,
      });
    }
    if (part.parent) {
      partHierarchy.push(part.parent.name);
      partHierarchyObjects.push({
        id: part.parent.id,
        parentId: part.parent.parentId,
      });
    }
    partHierarchy.push(part.name);
    partHierarchyObjects.push({ id: part.id, parentId: part.parentId });
  }

  return {
    catalogueId: catalogue?.id ?? null,
    catalogueName: catalogue?.name ?? "Catalogue inconnu",
    partId: part?.id ?? null,
    partHierarchy: partHierarchy.join(" > "),
    partHierarchyObjects,
  };
};

// Detect which suggestion is the currently applied one (auto-fill or manual)
const isSuggestionApplied = (
  suggestionDisorderId: number,
  suggestionGradeId: number,
) => {
  return (
    form.disorderTemplateId === suggestionDisorderId &&
    form.disorderGradeId === suggestionGradeId
  );
};

// Apply a suggestion: fills the form with its disorder/grade data
const applySuggestion = (
  suggestion: (typeof iqoaSuggestions.value)[number],
) => {
  if (!suggestion.disorderTemplate || !suggestion.disorderGrade) return;
  applyDisorderGrade(suggestion.disorderTemplate, suggestion.disorderGrade);
};

// Navigate to a suggestion's disorder in the catalogue tab
const navigateToSuggestionDisorder = (
  suggestion: (typeof iqoaSuggestions.value)[number],
) => {
  const template = suggestion.disorderTemplate;
  if (!template) return;
  const info = buildPartInfo(template);
  if (!info.catalogueId || !info.partId) return;

  CatalogueSessionState.navigateToDisorder(
    info.catalogueId,
    info.partId,
    template.id,
    info.partHierarchyObjects,
  );
  setActiveTab("catalogue");
};
</script>

<template>
  <el-dialog
    class="update-finding-dialog"
    v-model="show"
    fullscreen
    :show-close="false"
    append-to-body
  >
    <template #header><div></div></template>
    <template #default>
      <div class="dialog-content">
        <!-- Left panel: Tabs + content -->
        <div class="left-panel">
          <!-- Tab headers -->
          <div class="panel-tabs d-flex">
            <button
              class="tab-btn tab-images"
              :class="{ active: activeTab === 'images' }"
              @click="setActiveTab('images')"
            >
              <i class="bi bi-images me-2 tab-icon"></i>
              {{ t("report:findings.tabs.images") }}
            </button>
            <button
              class="tab-btn tab-catalogue"
              :class="{ active: activeTab === 'catalogue' }"
              @click="setActiveTab('catalogue')"
            >
              <i class="bi bi-book me-2 tab-icon"></i>
              {{ t("report:findings.tabs.catalogue") }}
            </button>
          </div>

          <!-- Tab content -->
          <div
            class="panel-content"
            :class="{ 'panel-content-catalogue': activeTab === 'catalogue' }"
          >
            <FindingImagesTab
              v-if="activeTab === 'images'"
              :finding-id="finding.id"
              :diagnostic-id="diagnosticId"
              :report-id="reportId"
              :section-id="sectionId"
              :images="currentFindingImages"
            />
            <CatalogueSearchTab
              v-else-if="activeTab === 'catalogue'"
              :selected-disorder-id="form.disorderTemplateId ?? undefined"
              :selected-grade-id="form.disorderGradeId ?? undefined"
              @select-grade="applyDisorderGrade"
              @select-disorder="applyDisorder"
            />
          </div>
        </div>

        <!-- Right panel: Form area -->
        <div class="right-panel">
          <!-- Header with title and close button -->
          <div class="panel-header">
            <div class="h5 mb-0">
              {{ t("report:diagnostics.finding.editDialog.title") }}
            </div>
            <DRBtnIcon
              variant="flat"
              icon="bi-x-lg"
              :size="36"
              @click="show = false"
            />
          </div>

          <div class="form-content">
            <!-- IQOA Suggestions (collapsible badge → list of yellow cards) -->
            <div
              v-if="hasIQOASuggestions"
              class="iqoa-suggestions"
              :class="{ expanded: isSuggestionsExpanded }"
            >
              <!-- Compact badge button (always visible) -->
              <button
                type="button"
                class="suggestions-badge"
                @click="isSuggestionsExpanded = !isSuggestionsExpanded"
              >
                <i class="bi-stars"></i>
                <span class="badge-label">
                  {{
                    t("report:findings.iqoaAssociation.suggestionsBadge", {
                      count: iqoaSuggestions.length,
                    })
                  }}
                </span>
                <i
                  class="bi badge-chevron"
                  :class="
                    isSuggestionsExpanded ? 'bi-chevron-up' : 'bi-chevron-down'
                  "
                ></i>
              </button>

              <!-- Expandable list of suggestion cards -->
              <div v-show="isSuggestionsExpanded" class="suggestions-list">
                <div
                  v-for="suggestion in iqoaSuggestions"
                  :key="suggestion.id"
                  class="suggestion-card"
                >
                  <!-- Top row: grade + title + confidence -->
                  <div class="suggestion-card-header">
                    <DisorderGradeBadge
                      v-if="suggestion.disorderGrade"
                      :value="suggestion.disorderGrade.class"
                      size="md"
                    />
                    <div class="suggestion-card-main">
                      <div class="suggestion-card-title-row">
                        <span
                          v-if="suggestion.disorderTemplate?.code"
                          class="disorder-code"
                        >
                          {{ suggestion.disorderTemplate.code }}
                        </span>
                        <span class="disorder-title">
                          {{ suggestion.disorderTemplate?.title }}
                        </span>
                      </div>
                      <div
                        v-if="suggestion.disorderTemplate"
                        class="suggestion-card-meta"
                      >
                        {{
                          buildPartInfo(suggestion.disorderTemplate)
                            .catalogueName
                        }}
                        ›
                        {{
                          buildPartInfo(suggestion.disorderTemplate)
                            .partHierarchy
                        }}
                      </div>
                    </div>
                    <div class="suggestion-confidence">
                      <div class="confidence-value">
                        {{ Math.round(suggestion.confidence * 100) }}%
                      </div>
                      <div class="confidence-label">
                        {{ t("report:findings.iqoaAssociation.confidence") }}
                      </div>
                    </div>
                  </div>

                  <!-- Reasoning -->
                  <div class="suggestion-reasoning">
                    <div class="reasoning-label">
                      <i class="bi-chat-left-text me-1"></i>
                      {{ t("report:findings.iqoaAssociation.reasoning") }}
                    </div>
                    <div class="reasoning-text">{{ suggestion.reasoning }}</div>
                  </div>

                  <!-- Missing info (optional) -->
                  <div v-if="suggestion.missingInfo" class="suggestion-missing">
                    <div class="missing-label">
                      <i class="bi-info-circle me-1"></i>
                      {{ t("report:findings.iqoaAssociation.missingInfo") }}
                    </div>
                    <div class="missing-text">{{ suggestion.missingInfo }}</div>
                  </div>

                  <!-- Actions -->
                  <div class="suggestion-actions">
                    <DRBtn
                      size="sm"
                      variant="outline"
                      icon="bi-eye"
                      :label="
                        t('report:findings.iqoaAssociation.viewInCatalogue')
                      "
                      @click="navigateToSuggestionDisorder(suggestion)"
                    />
                    <DRBtn
                      size="sm"
                      :variant="
                        isSuggestionApplied(
                          suggestion.disorderTemplateId,
                          suggestion.disorderGradeId,
                        )
                          ? 'primary'
                          : 'outline'
                      "
                      :icon="
                        isSuggestionApplied(
                          suggestion.disorderTemplateId,
                          suggestion.disorderGradeId,
                        )
                          ? 'bi-check-lg'
                          : 'bi-check'
                      "
                      :label="
                        isSuggestionApplied(
                          suggestion.disorderTemplateId,
                          suggestion.disorderGradeId,
                        )
                          ? t('report:findings.iqoaAssociation.applied')
                          : t('report:findings.iqoaAssociation.applySuggestion')
                      "
                      :disabled="
                        isSuggestionApplied(
                          suggestion.disorderTemplateId,
                          suggestion.disorderGradeId,
                        )
                      "
                      @click="applySuggestion(suggestion)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <el-form label-position="top" class="row">
              <el-form-item :label="t('report:diagnostics.finding.item.name')">
                <el-input
                  size="large"
                  v-model="form.name"
                  :placeholder="t('report:diagnostics.finding.item.name')"
                >
                </el-input>
              </el-form-item>
              <el-form-item>
                <template #label>
                  <div
                    class="d-flex align-items-center justify-content-between w-100"
                  >
                    <span>{{
                      t("report:diagnostics.finding.item.observation")
                    }}</span>
                    <button
                      v-if="sourceObservations.length"
                      type="button"
                      class="source-obs-link"
                      @click="showObservationDrawer = true"
                    >
                      <i class="bi-binoculars" />
                      {{ t("report:findings.sourceObservation.button") }}
                      <span v-if="sourceObservations.length > 1">
                        ({{ sourceObservations.length }})
                      </span>
                    </button>
                  </div>
                </template>
                <el-input
                  size="large"
                  v-model="form.comment"
                  type="textarea"
                  :placeholder="
                    t('report:diagnostics.finding.item.observation')
                  "
                  :rows="4"
                >
                </el-input>
              </el-form-item>
              <el-form-item
                :label="t('report:diagnostics.finding.item.number')"
              >
                <el-input
                  size="large"
                  v-model="form.defaultNumbering"
                  :placeholder="t('report:diagnostics.finding.item.number')"
                />
              </el-form-item>
              <el-form-item
                class="col-12"
                :label="t('report:diagnostics.finding.item.location')"
              >
                <el-input
                  size="large"
                  v-model="form.location"
                  :placeholder="
                    sectionId
                      ? defaultLocationPath
                      : t('report:photos.editDialog.locationPlaceholder')
                  "
                />
                <div
                  v-if="sectionId && !form.location.trim()"
                  class="form-text text-muted"
                >
                  {{ t("report:photos.editDialog.locationHint") }}
                </div>
              </el-form-item>
              <!-- Notation, Evolution, Security on same line -->
              <div class="col-12 d-flex justify-content-between gap-4 mb-4">
                <!-- Notation IQOA -->
                <div class="field-group">
                  <div
                    class="d-flex align-items-center justify-content-between gap-3 mb-2"
                  >
                    <label class="field-label mb-0">
                      {{ t("report:diagnostics.finding.item.notation") }}
                    </label>
                    <NotationSystemBadge
                      :model-value="systemCode"
                      @update:model-value="handleSystemChange"
                    />
                  </div>
                  <el-input
                    v-if="isCustom"
                    v-model="form.notation"
                    :placeholder="
                      t(
                        'report:diagnostics.finding.item.notationCustomPlaceholder',
                      )
                    "
                  />
                  <div v-else class="notation-badges">
                    <div
                      v-for="option in notationOptions"
                      :key="option"
                      class="notation-badge-wrapper"
                      :class="{ inactive: form.notation !== option }"
                      @click="toggleNotation(option)"
                    >
                      <DisorderGradeBadge :value="option" size="lg" />
                    </div>
                  </div>
                </div>

                <!-- Evolution -->
                <div class="field-group">
                  <label class="field-label d-flex align-items-center gap-1">
                    {{ t("report:diagnostics.finding.item.evolution") }}
                    <el-tooltip
                      :content="
                        t('report:diagnostics.finding.item.evolutionHelp')
                      "
                      placement="top"
                    >
                      <el-icon class="help-icon"
                        ><i class="bi-question-circle"></i
                      ></el-icon>
                    </el-tooltip>
                  </label>
                  <div class="toggle-buttons">
                    <button
                      type="button"
                      class="toggle-btn"
                      :class="{ active: form.evolution === false }"
                      @click="form.evolution = false"
                    >
                      {{ t("no") }}
                    </button>
                    <button
                      type="button"
                      class="toggle-btn active-warning"
                      :class="{ active: form.evolution === true }"
                      @click="form.evolution = true"
                    >
                      {{ t("yes") }}
                    </button>
                  </div>
                </div>

                <!-- Security -->
                <div class="field-group">
                  <label class="field-label d-flex align-items-center gap-1">
                    {{ t("report:diagnostics.finding.item.security") }}
                    <el-tooltip
                      :content="
                        t('report:diagnostics.finding.item.securityHelp')
                      "
                      placement="top"
                    >
                      <el-icon class="help-icon"
                        ><i class="bi-question-circle"></i
                      ></el-icon>
                    </el-tooltip>
                  </label>
                  <div class="toggle-buttons">
                    <button
                      type="button"
                      class="toggle-btn"
                      :class="{ active: form.security === false }"
                      @click="form.security = false"
                    >
                      {{ t("no") }}
                    </button>
                    <button
                      type="button"
                      class="toggle-btn active-warning"
                      :class="{ active: form.security === true }"
                      @click="form.security = true"
                    >
                      {{ t("yes") }}
                    </button>
                  </div>
                </div>
              </div>
            </el-form>
          </div>

          <div class="form-footer">
            <DRBtn
              variant="primary"
              size="lg"
              @click="confirm"
              :label="t('report:findings.saveInfo')"
            />
          </div>
        </div>
      </div>
    </template>
  </el-dialog>

  <!-- Source observations drawer -->
  <SourceObservationDrawer
    v-if="sourceObservations.length"
    v-model="showObservationDrawer"
    :observations="sourceObservations"
  />
</template>

<style lang="scss">
.source-obs-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--bs-primary);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  opacity: 0.8;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
}

.form-label,
.update-finding-dialog .el-form-item__label {
  font-weight: 700;
  font-size: 0.9rem;
}

.update-finding-dialog.el-dialog.is-fullscreen {
  background-color: transparent !important;
  display: flex;
  flex-direction: column;
}

.update-finding-dialog .el-dialog__header {
  display: none !important;
}

.update-finding-dialog .el-dialog__body {
  padding: 24px !important;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.update-finding-dialog .dialog-content {
  display: flex;
  flex: 1;
  gap: 24px;
  overflow: hidden;
}

.update-finding-dialog .left-panel {
  flex: 1;
  min-width: 400px;
  max-width: 55%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background-color: var(--el-bg-color, #fff);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.update-finding-dialog .panel-tabs {
  flex-shrink: 0;
  background-color: var(--el-bg-color, #fff);
  display: flex;
  gap: 0;
}

.update-finding-dialog .tab-btn {
  flex: 1;
  padding: 12px 24px;
  border: none;
  background: transparent;
  font-weight: 500;
  color: var(--bs-gray-600);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(.active) {
    background-color: var(--bs-gray-200);
  }

  // Images tab (blue theme)
  &.tab-images.active {
    background-color: var(--bs-primary);
    color: var(--bs-white);
  }

  // Catalogue tab (yellow theme)
  &.tab-catalogue.active {
    background-color: var(--catalogue-border);
    color: var(--catalogue-text);
  }

  .tab-icon {
    font-size: 1.25rem;
  }
}

.update-finding-dialog .panel-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--el-border-color, #ebeef5);

  &.panel-content-catalogue {
    border: 2px solid var(--catalogue-border);
    border-top: none;
  }
}

.update-finding-dialog .right-panel {
  flex: 1;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background-color: var(--el-bg-color, #fff);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.update-finding-dialog .panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--el-border-color, #ebeef5);
  background-color: var(--bs-gray-100);
}

.update-finding-dialog .form-content {
  flex: 1;
  overflow: auto;
  padding: 24px;
}

.update-finding-dialog .form-footer {
  flex-shrink: 0;
  padding: 16px 24px;
  border-top: 1px solid var(--el-border-color, #ebeef5);
  display: flex;
  justify-content: center;
}

/* Field group styling */
.update-finding-dialog .field-group {
  .field-label {
    display: block;
    font-weight: 700;
    font-size: 0.9rem;
    margin-bottom: 8px;
    color: var(--el-text-color-regular);
  }

  .notation-badges {
    display: flex;
    gap: 8px;
  }

  .notation-badge-wrapper {
    cursor: pointer;
    transition: all 0.15s ease;
    border-radius: 6px;
    padding: 2px;

    &:hover {
      transform: scale(1.1);
    }

    &.inactive {
      opacity: 0.4;
      filter: grayscale(100%);
    }
  }

  .help-icon {
    color: var(--bs-gray-500);
    font-size: 14px;
    cursor: help;
  }

  .toggle-buttons {
    display: flex;
    gap: 8px;
  }

  .toggle-btn {
    min-width: 50px;
    height: 36px;
    padding: 0 16px;
    border: none;
    border-radius: 6px;
    background-color: var(--bs-gray-300);
    color: var(--bs-gray-600);
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background-color: var(--bs-gray-400);
    }

    &.active {
      background-color: var(--bs-gray-600);
      color: white;
    }

    &.active-warning.active {
      background-color: #fd7e14;
      color: white;
    }
  }
}

/* ============================================================
   IQOA Suggestions — badge + expandable yellow card list
   ============================================================ */
.update-finding-dialog .iqoa-suggestions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

/* Compact badge button */
.update-finding-dialog .suggestions-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 6px 14px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: white;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(139, 92, 246, 0.3);
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(139, 92, 246, 0.45);
    transform: translateY(-1px);
  }

  i {
    font-size: 14px;
  }

  .badge-chevron {
    font-size: 12px;
    opacity: 0.85;
  }
}

/* Expandable list */
.update-finding-dialog .suggestions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Yellow card per suggestion */
.update-finding-dialog .suggestion-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(
    135deg,
    rgba(254, 243, 199, 0.65) 0%,
    rgba(253, 230, 138, 0.55) 100%
  );
  border: 1px solid rgba(217, 119, 6, 0.35);
  border-radius: 12px;
  transition: all 0.2s ease;
}

.update-finding-dialog .suggestion-card-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.update-finding-dialog .suggestion-card-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.update-finding-dialog .suggestion-card-title-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.update-finding-dialog .suggestion-card .disorder-code {
  font-weight: 600;
  color: #92400e;
  font-size: 13px;
}

.update-finding-dialog .suggestion-card .disorder-title {
  font-weight: 600;
  color: var(--el-text-color-primary);
  font-size: 14px;
}

.update-finding-dialog .suggestion-card-meta {
  font-size: 11px;
  color: var(--el-text-color-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Confidence chip on the right */
.update-finding-dialog .suggestion-confidence {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background-color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(217, 119, 6, 0.35);
  border-radius: 8px;
  min-width: 64px;
}

.update-finding-dialog .confidence-value {
  font-size: 18px;
  font-weight: 700;
  color: #92400e;
  line-height: 1;
}

.update-finding-dialog .confidence-label {
  font-size: 9px;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
  opacity: 0.75;
}

/* Reasoning section */
.update-finding-dialog .suggestion-reasoning {
  padding: 10px 12px;
  background-color: rgba(255, 255, 255, 0.55);
  border-radius: 8px;
}

.update-finding-dialog .reasoning-label {
  font-size: 10px;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.update-finding-dialog .reasoning-text {
  font-size: 13px;
  color: var(--el-text-color-regular);
  line-height: 1.5;
}

/* Missing info section */
.update-finding-dialog .suggestion-missing {
  padding: 8px 12px;
  background-color: rgba(254, 215, 170, 0.5);
  border-left: 3px solid #d97706;
  border-radius: 4px;
}

.update-finding-dialog .missing-label {
  font-size: 10px;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 2px;
}

.update-finding-dialog .missing-text {
  font-size: 12px;
  color: var(--el-text-color-regular);
  font-style: italic;
  line-height: 1.4;
}

/* Action buttons */
.update-finding-dialog .suggestion-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
</style>
